import { ReactNode } from "react";
import { Box, SxProps, TableBody, TableHead, TableRow, Theme, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { IntensityMetric, ReportData } from "../../../models/dto/cat15/cat15.types";
import CollapsibleSection from "../../../components/cat15/CollapsibleSection";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import {
  assetClassLabelKey,
  INTENSITY_METRICS,
  intensityMetricLabelKey,
} from "../../../components/cat15/labels";
import { formatDate, formatNumber, formatTHB } from "../format";

interface Props {
  data: ReportData;
}

// The same four notes the server prints into the PDF and XLSX, kept here so the screen shows them in its own language.
const BASIS_NOTES = [
  ["Basis-Note-Formula-Label", "Basis-Note-Formula"],
  ["Basis-Note-Outstanding-Label", "Basis-Note-Outstanding"],
  ["Basis-Note-Revenue-Label", "Basis-Note-Revenue"],
  ["Basis-Note-Project-Label", "Basis-Note-Project"],
] as const;

/** FeTable wrapper — rounded, bordered container for a report section table. */
const RTable = ({ children }: { children: ReactNode }) => (
  <FeTableWrap sx={{ border: "1px solid var(--gray5)" }}>
    <FeTableScroll>
      <FeTable>{children}</FeTable>
    </FeTableScroll>
  </FeTableWrap>
);

// Both Data Gap tables share this grid so their stacked headers line up; the last column is widest because it holds sentences.
const GAP_COL = { borrower: "28%", loan: "14%", outstanding: "18%", reason: "40%" };
// the reason column keeps its own left gutter, or the right-aligned figures beside it crowd the text
const GAP_REASON_PL = "28px";

// Bold, un-zebra'd summary/total row at the foot of a table (Figma "Total" /
// "Portfolio Weighted" rows). !important beats FeRow's nth-of-type striping.
const totalRowSx: SxProps<Theme> = {
  "& td": {
    fontWeight: 700,
    backgroundColor: "var(--white) !important",
    borderTop: "1px solid var(--gray5)",
  },
};

const KeyVal = ({ label, value, labelSx }: { label: string; value: string; labelSx?: SxProps<Theme> }) => (
  <Box>
    <Typography variant="body2" color="var(--gray9)" sx={labelSx}>
      {label}
    </Typography>
    <Typography fontSize="14px" lineHeight="24px" fontWeight={500} color="var(--dark)">
      {value}
    </Typography>
  </Box>
);

/** Sub-heading + body paragraph used in the Methodology section. */
const MethodBlock = ({ title, body }: { title: string; body: string }) => (
  <Box mt="12px">
    <Typography variant="body2" fontWeight={700} color="var(--dark)" mb="4px">
      {title}
    </Typography>
    <Typography variant="body2" color="var(--dark)" sx={{ whiteSpace: "pre-line" }}>
      {body}
    </Typography>
  </Box>
);

// Signed delta text for a KPI, e.g. "+8.2%" / "-0.3" / "—".
const deltaText = (delta: number | null, unit: string): string => {
  if (delta == null) return "—";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${formatNumber(delta)}${unit}`;
};

const dashNumber = (value: number | null): string => (value == null ? "—" : formatNumber(value));

const ReportPreview = ({ data }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  // Portfolio total outstanding (M baht) — denominator for the derived
  // "% of Total" columns the design shows on the exclusion / data-gap tables.
  const totalOutstandingM = data.absolute.reduce((s, r) => s + r.outstandingM, 0);
  const pctOfPortfolio = (m: number): string =>
    totalOutstandingM > 0 ? `${formatNumber((m / totalOutstandingM) * 100)}%` : "—";

  // §Absolute totals — bold footer row.
  const absTotal = data.absolute.reduce(
    (a, r) => ({ loans: a.loans + r.loans, outM: a.outM + r.outstandingM, fin: a.fin + r.financed }),
    { loans: 0, outM: 0, fin: 0 },
  );
  // §Sector totals.
  const secTotal = data.sectors.reduce(
    (a, r) => ({ loans: a.loans + r.loans, outM: a.outM + r.outstandingM, fin: a.fin + r.financed }),
    { loans: 0, outM: 0, fin: 0 },
  );
  // §Intensity totals — the per-basis cells come from the server's weighted figures, not these sums.
  const intTotal = data.intensity.reduce(
    (a, r) => ({ outM: a.outM + r.outstandingM, fin: a.fin + r.financed }),
    { outM: 0, fin: 0 },
  );
  const basisLabel = (metric: IntensityMetric) => t(intensityMetricLabelKey(metric));
  const basisCoverage = data.intensityCoverage;

  // §Coverage — gap to the 80% coverage target (by count), e.g. "-1.5 pp".
  const coverageGap = data.coverageByCount - 80;

  // §DQS — pivot the portfolio distribution into DQS 1..5 columns.
  const dqsPct = (level: number): string => {
    const item = data.dqsDistribution.find((d) => d.level === level);
    return item ? `${formatNumber(item.percent)}%` : "—";
  };

  // §Data Gap — excluded-loan summary derived from the coverage exclusions.
  const excludedM = data.exclusions.reduce((s, r) => s + r.outstandingM, 0);
  const dataGapSummary =
    data.exclusions.length > 0
      ? `${t("DataGap-Summary")} ${data.exclusions
          .map((r) => `${r.loans} ${r.reason}`)
          .join(" · ")}. ${t("DataGap-Total-Excluded")}: ${formatNumber(excludedM)} ${t(
          // the figure is summed straight from the rows, which carry baht — the same scale the table above prints
          "Unit-Baht",
        )} (${pctOfPortfolio(excludedM)} ${t("Of-Portfolio")})`
      : "";

  const sections: { title: string; open?: boolean; body: ReactNode }[] = [];

  // §1 Cover
  sections.push({
    title: t("Sec-Cover"),
    open: true,
    body: (
      <Grid container spacing="16px">
        {[
          [t("Institution"), data.institution],
          [t("Period"), data.period],
          [t("Methodology"), data.methodology],
          [t("Total-Financed-Emissions"), `${formatNumber(data.totalEmissions)} tCO₂e`],
          [t("KPI-Outstanding"), formatTHB(data.outstanding)],
          [t("KPI-Coverage"), `${formatNumber(data.coverage)}%`],
          [t("Weighted-DQS"), `${formatNumber(data.weightedDqs)} / 5`],
          [t("Generated"), formatDate(data.generatedAt)],
        ].map(([label, value]) => (
          <Grid xs={6} md={3} key={label}>
            <KeyVal label={label} value={value} />
          </Grid>
        ))}
      </Grid>
    ),
  });

  // §Absolute Financed Emissions
  sections.push({
    title: t("Sec-Absolute"),
    body: (
      <RTable>
        <TableHead>
          <TableRow>
            <FeHeadCell>{t("Col-Asset-Class")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Loans")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Outstanding-M")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Financed")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Pct-Total")}</FeHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.absolute.map((r) => (
            <FeRow key={r.assetClass}>
              <FeCell>{t(assetClassLabelKey(r.assetClass))}</FeCell>
              <FeCell align="right">{r.loans}</FeCell>
              <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
              <FeCell align="right">{formatNumber(r.financed)}</FeCell>
              <FeCell align="right">{formatNumber(r.percent)}%</FeCell>
            </FeRow>
          ))}
          <TableRow sx={totalRowSx}>
            <FeCell>{t("Total")}</FeCell>
            <FeCell align="right">{absTotal.loans}</FeCell>
            <FeCell align="right">{formatNumber(absTotal.outM)}</FeCell>
            <FeCell align="right">{formatNumber(absTotal.fin)}</FeCell>
            <FeCell align="right">100%</FeCell>
          </TableRow>
        </TableBody>
      </RTable>
    ),
  });

  // §Emission Intensity
  sections.push({
    title: t("Sec-Intensity"),
    body: (
      <>
        <RTable>
          <TableHead>
            <TableRow>
              <FeHeadCell>{t("Col-Asset-Class")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Outstanding-M")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Financed")}</FeHeadCell>
              {INTENSITY_METRICS.map((m) => (
                <FeHeadCell key={m} align="right">
                  {t("Col-Per-Basis", { basis: basisLabel(m) })}
                </FeHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.intensity.map((r) => (
              <FeRow key={r.assetClass}>
                <FeCell>{t(assetClassLabelKey(r.assetClass))}</FeCell>
                <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
                <FeCell align="right">{formatNumber(r.financed)}</FeCell>
                {INTENSITY_METRICS.map((m) => (
                  <FeCell key={m} align="right">{dashNumber(r.intensityByMetric[m])}</FeCell>
                ))}
              </FeRow>
            ))}
            <TableRow sx={totalRowSx}>
              <FeCell>{t("Weighted-Total")}</FeCell>
              <FeCell align="right">{formatNumber(intTotal.outM)}</FeCell>
              <FeCell align="right">{formatNumber(intTotal.fin)}</FeCell>
              {INTENSITY_METRICS.map((m) => (
                <FeCell key={m} align="right">{dashNumber(data.intensityTotal[m])}</FeCell>
              ))}
            </TableRow>
          </TableBody>
        </RTable>
        <Typography variant="body2" color="var(--gray9)" mt="12px">
          {t("Intensity-Unit-Note")}
        </Typography>
        {BASIS_NOTES.map(([label, note]) => (
          <Typography key={note} variant="body2" color="var(--dark)" mt="6px">
            {`${t(label)}: ${t(note)}`}
          </Typography>
        ))}
      </>
    ),
  });

  // §Coverage Disclosure
  sections.push({
    title: t("Sec-Coverage"),
    body: (
      <>
        <Box display="flex" flexWrap="wrap" gap="32px" mb="16px">
          <KeyVal label={t("Coverage-By-Count")} value={`${formatNumber(data.coverageByCount)}%`} />
          <KeyVal label={t("Coverage-By-Outstanding")} value={`${formatNumber(data.coverageByOutstanding)}%`} />
          <KeyVal label={t("Gap-80-Target")} value={`${deltaText(coverageGap, "")} ${t("Unit-PP")}`} />
        </Box>
        {basisCoverage && (
          <Box display="flex" flexWrap="wrap" gap="32px" mb="16px">
            {INTENSITY_METRICS.map((m) => (
              <KeyVal
                key={m}
                label={t("Coverage-Basis", { basis: basisLabel(m) })}
                value={t("Coverage-Basis-Value", {
                  loans: basisCoverage[m].loans,
                  total: basisCoverage[m].totalLoans,
                  pct: formatNumber(basisCoverage[m].outstandingPct),
                })}
              />
            ))}
          </Box>
        )}
        <RTable>
          <TableHead>
            <TableRow>
              <FeHeadCell>{t("Col-Exclusion-Reason")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Loans")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Outstanding-M")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Pct-Total")}</FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.exclusions.map((r) => (
              <FeRow key={r.reason}>
                <FeCell>{r.reason}</FeCell>
                <FeCell align="right">{r.loans}</FeCell>
                <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
                <FeCell align="right">{pctOfPortfolio(r.outstandingM)}</FeCell>
              </FeRow>
            ))}
          </TableBody>
        </RTable>
      </>
    ),
  });

  // §Data Quality Score (DQS) — DQS 1..5 columns + weighted score.
  sections.push({
    title: t("Sec-DQS"),
    body: (
      <>
        <RTable>
          <TableHead>
            <TableRow>
              <FeHeadCell>{t("Col-Asset-Class")}</FeHeadCell>
              {[1, 2, 3, 4, 5].map((lv) => (
                <FeHeadCell key={lv} align="right">{`DQS ${lv}`}</FeHeadCell>
              ))}
              <FeHeadCell align="right">{t("Col-Weighted")}</FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={totalRowSx}>
              <FeCell>{t("Portfolio-Weighted")}</FeCell>
              {[1, 2, 3, 4, 5].map((lv) => (
                <FeCell key={lv} align="right">{dqsPct(lv)}</FeCell>
              ))}
              <FeCell align="right" sx={{ color: "var(--green) !important" }}>
                {formatNumber(data.weightedDqs)}
              </FeCell>
            </TableRow>
          </TableBody>
        </RTable>
        <Typography variant="body2" color="var(--gray9)" mt="12px">
          {t("Weighted-DQS")}: {formatNumber(data.weightedDqs)} / 5
        </Typography>
      </>
    ),
  });

  // §Sector Breakdown (ISIC)
  sections.push({
    title: t("Sec-Sector"),
    body: (
      <RTable>
        <TableHead>
          <TableRow>
            <FeHeadCell>{t("Col-Sector")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Loans")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Outstanding-M")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Financed")}</FeHeadCell>
            <FeHeadCell align="right">{t("Col-Pct-Total")}</FeHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.sectors.map((r) => (
            <FeRow key={r.sector}>
              <FeCell>{r.sector}</FeCell>
              <FeCell align="right">{r.loans}</FeCell>
              <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
              <FeCell align="right">{formatNumber(r.financed)}</FeCell>
              <FeCell align="right">{formatNumber(r.percent)}%</FeCell>
            </FeRow>
          ))}
          <TableRow sx={totalRowSx}>
            <FeCell>{t("Total")}</FeCell>
            <FeCell align="right">{secTotal.loans}</FeCell>
            <FeCell align="right">{formatNumber(secTotal.outM)}</FeCell>
            <FeCell align="right">{formatNumber(secTotal.fin)}</FeCell>
            <FeCell align="right">100%</FeCell>
          </TableRow>
        </TableBody>
      </RTable>
    ),
  });

  // §Methodology
  sections.push({
    title: t("Sec-Methodology"),
    body: (
      <>
        <Typography variant="body2" color="var(--dark)">
          {t("Methodology-Intro")}
        </Typography>
        <Typography variant="body2" fontWeight={700} color="var(--dark)" mt="12px">
          {t("Methodology-Formulas-Title")}
        </Typography>
        <MethodBlock title={t("Methodology-Share-Title")} body={t("Methodology-Share-Body")} />
        <MethodBlock title={t("Methodology-Reported-Title")} body={t("Methodology-Reported-Body")} />
        <MethodBlock title={t("Methodology-Estimates-Title")} body={t("Methodology-Estimates-Body")} />
        <MethodBlock title={t("Methodology-Scope-Title")} body={t("Methodology-Scope-Body")} />
        <MethodBlock title={t("Methodology-Revenue-Title")} body={t("Methodology-Revenue-Body")} />
        <MethodBlock title={t("Methodology-YearTol-Title")} body={t("Methodology-YearTol-Body")} />
        <MethodBlock title={t("Methodology-Currency-Title")} body={t("Methodology-Currency-Body")} />
        <MethodBlock title={t("Methodology-DQS-Title")} body={t("Methodology-DQS-Body")} />
      </>
    ),
  });

  // §Data Gap
  sections.push({
    title: t("Sec-DataGap"),
    body: (
      <>
        <RTable>
          <TableHead>
            <TableRow>
              <FeHeadCell sx={{ width: GAP_COL.borrower }}>{t("Col-Borrower")}</FeHeadCell>
              <FeHeadCell sx={{ width: GAP_COL.loan }}>{t("Col-Loan-Ref")}</FeHeadCell>
              <FeHeadCell align="right" sx={{ width: GAP_COL.outstanding }}>{t("Col-Outstanding-M")}</FeHeadCell>
              <FeHeadCell sx={{ width: GAP_COL.reason, pl: GAP_REASON_PL }}>{t("Col-Reason")}</FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.dataGap.map((r) => (
              <FeRow key={r.loanRef}>
                <FeCell>{r.borrower}</FeCell>
                <FeCell>{r.loanRef}</FeCell>
                <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
                <FeCell sx={{ pl: GAP_REASON_PL, whiteSpace: "normal", color: "var(--gray1)" }}>{r.reason}</FeCell>
              </FeRow>
            ))}
          </TableBody>
        </RTable>
        {dataGapSummary && (
          <Box mt="12px" p="10px 12px" borderRadius="8px" bgcolor="var(--report-warn-bg)">
            <Typography fontSize="14px" lineHeight="20px" fontWeight={500} color="var(--status-pending-text)">
              {dataGapSummary}
            </Typography>
          </Box>
        )}
        {data.missingFiguresCount > 0 && (
          <Box mt="16px">
            <Typography variant="body2" fontWeight={700} color="var(--dark)" mb="8px">
              {t("DataGap-Missing-Figures-Title", { count: data.missingFiguresCount })}
            </Typography>
            <RTable>
              <TableHead>
                <TableRow>
                  <FeHeadCell sx={{ width: GAP_COL.borrower }}>{t("Col-Borrower")}</FeHeadCell>
                  <FeHeadCell sx={{ width: GAP_COL.loan }}>{t("Col-Loan-Ref")}</FeHeadCell>
                  <FeHeadCell align="right" sx={{ width: GAP_COL.outstanding }}>{t("Col-Outstanding-M")}</FeHeadCell>
                  <FeHeadCell sx={{ width: GAP_COL.reason, pl: GAP_REASON_PL }}>{t("Col-Missing-Figures")}</FeHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.missingFigures.map((r, i) => (
                  <FeRow key={`${r.borrower}-${i}`}>
                    <FeCell>{r.borrower}</FeCell>
                    {/* a missing figure belongs to the borrower, not to any one loan */}
                    <FeCell>—</FeCell>
                    <FeCell align="right">{formatNumber(r.outstandingM)}</FeCell>
                    <FeCell sx={{ pl: GAP_REASON_PL, whiteSpace: "normal", color: "var(--gray1)" }}>
                      {r.metrics.map((m) => basisLabel(m)).join(", ")}
                    </FeCell>
                  </FeRow>
                ))}
              </TableBody>
            </RTable>
            {data.missingFigures.length < data.missingFiguresCount && (
              <Typography variant="body2" color="var(--gray9)" mt="8px">
                {t("DataGap-Showing", { shown: data.missingFigures.length, total: data.missingFiguresCount })}
              </Typography>
            )}
          </Box>
        )}
      </>
    ),
  });

  // §Footnotes
  sections.push({
    title: t("Sec-Footnotes"),
    body: (
      <Box component="ol" sx={{ m: 0, pl: "20px" }}>
        {data.footnotes.map((f, i) => (
          <Typography key={i} component="li" variant="body2" color="var(--gray9)" mb="6px">
            {f}
          </Typography>
        ))}
      </Box>
    ),
  });

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {sections.map((s, i) => (
        <CollapsibleSection key={s.title} index={i + 1} title={s.title} defaultOpen={s.open}>
          {s.body}
        </CollapsibleSection>
      ))}
    </Box>
  );
};

export default ReportPreview;
